import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet,
  View,
  FlatList,
  Alert,
  RefreshControl,
  TouchableOpacity,
  Text as RNText,
  Platform,
} from 'react-native';
import {
  TextInput,
  Button,
  Card,
  Text,
  IconButton,
  Dialog,
  Portal,
  FAB,
} from 'react-native-paper';
import axios from 'axios';

const RAILWAY_URL = 'https://radyohub-production.up.railway.app';
const API_BASE_URL = `${RAILWAY_URL}/api`;

export default function RoomList({ radio, onClose, onSelectRoom, userName = 'Kullanıcı', tokens = 0, spendTokens, showRewardedAd, isAdsenseConfigured = false, onPlayRadio, onOpenPisti, onOpenBatak, onOpenUno }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const isCreatingRoomRef = useRef(false);
  const autoRoomCreatedRef = useRef(false); // Otomatik oda oluşturuldu mu?
  const hasStartedRadioRef = useRef(false); // Radyo başlatıldı mı?

  useEffect(() => {
    autoRoomCreatedRef.current = false; // Yeni radyo için reset
    hasStartedRadioRef.current = false; // Yeni radyo için reset
    loadRooms();
    // Periyodik olarak odaları güncelle (her 3 saniyede bir)
    const interval = setInterval(loadRooms, 3000);
    return () => clearInterval(interval);
  }, [radio._id]);

  // Oda listesine girildiğinde radyo çalmaya başlat
  useEffect(() => {
    if (onPlayRadio && radio?.streamUrl && !hasStartedRadioRef.current) {
      hasStartedRadioRef.current = true;
      // Kısa bir gecikme ile radyo çalmaya başlat
      setTimeout(() => {
        onPlayRadio(radio).catch((error) => {
          console.error('[RoomList] Radyo çalma hatası:', error);
        });
      }, 300);
    }
  }, [radio, onPlayRadio]);

  // Birlikte dinle odalarına girildiğinde otomatik bot odası oluştur
  const createAutoBotRoom = async () => {
    try {
      // Rastgele oda ismi seç
      const roomNames = [
        "Sohbet Zamanı 💬",
        "Eğlence Modu 🎉",
        "Birlikte Dinle 🎵",
        "Sohbet Alanı 💭",
        "Eğlence Zamanı ✨",
      ];
      const randomRoomName = roomNames[Math.floor(Math.random() * roomNames.length)];
      
      const response = await axios.post(
        `${API_BASE_URL}/radios/${radio._id}/rooms`,
        { roomName: randomRoomName, autoCreate: true }, // Otomatik oda işareti
        { timeout: 5000 }
      );
      
      if (response.data && response.data.roomId) {
        // Odaları yeniden yükle (kullanıcıyı otomatik içeri alma)
        await loadRooms();
      }
    } catch (error) {
      console.error('Otomatik bot odası oluşturma hatası:', error);
    }
  };

  const loadRooms = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/radios/${radio._id}/rooms`, {
        timeout: 5000,
      });
      const roomsList = response.data || [];
      
      // Her radyo için oyun odalarını ekle
      const gameRooms = [
        {
          roomId: `game-${radio._id}-pisti`,
          roomName: 'Pişti 🃏',
          participantCount: 0,
          gameType: 'pisti',
          isGameRoom: true,
        },
        {
          roomId: `game-${radio._id}-uno`,
          roomName: 'UNO 🎴',
          participantCount: 0,
          gameType: 'uno',
          isGameRoom: true,
        },
        {
          roomId: `game-${radio._id}-batak`,
          roomName: 'Batak ♠️',
          participantCount: 0,
          gameType: 'batak',
          isGameRoom: true,
        },
      ];
      
      setRooms([...gameRooms, ...roomsList]);
      
      // Alem FM ve 90'lar için otomatik bot odası oluştur (sunucu başladığında hazırda bekliyor)
      const isAutoBotRadio = radio.name === 'Alem FM' || radio.name === "90'lar";
      
      // Eğer oda yoksa ve ilk yükleme ise ve daha önce oluşturulmadıysa ve otomatik bot radyosu ise otomatik bot odası oluştur
      if (roomsList.length === 0 && loading && !autoRoomCreatedRef.current && isAutoBotRadio) {
        autoRoomCreatedRef.current = true; // Flag'i set et
        // İlk yükleme tamamlandıktan sonra otomatik oda oluştur
        setTimeout(() => {
          createAutoBotRoom();
        }, 500);
      }
    } catch (error) {
      console.error('Oda listesi yükleme hatası:', error);
      // 404 hatası alındığında boş liste döndür (henüz oda yok)
      if (error.response?.status === 404) {
        setRooms([]);
        // Alem FM, Aşk FM ve 90'lar için otomatik bot odası oluştur
        const isAutoBotRadio = radio.name === 'Alem FM' || radio.name === 'Aşk FM' || radio.name === "90'lar";
        if (loading && !autoRoomCreatedRef.current && isAutoBotRadio) {
          autoRoomCreatedRef.current = true; // Flag'i set et
          setTimeout(() => {
            createAutoBotRoom();
          }, 500);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const createRoom = async () => {
    if (Platform.OS === 'web') {
      console.log('[Web RoomList] createRoom called, newRoomName:', newRoomName, 'tokens:', tokens);
    }
    
    // Eğer zaten oda oluşturma işlemi devam ediyorsa, yeni istekleri engelle
    if (isCreatingRoomRef.current) {
      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Room creation already in progress');
      }
      return;
    }

    const trimmedName = newRoomName.trim();
    if (!trimmedName) {
      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Room name is empty');
      }
      Alert.alert('Hata', 'Lütfen oda adı girin');
      return;
    }
    if (trimmedName.length < 3) {
      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Room name too short:', trimmedName.length);
      }
      Alert.alert('Hata', 'Oda adı en az 3 karakter olmalıdır.');
      return;
    }

    if (Platform.OS === 'web') {
      console.log('[Web RoomList] Checking tokens, current tokens:', tokens);
    }

    if (tokens < 1) {
      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Not enough tokens for room creation');
        // Web'de AdSense yapılandırılmamışsa veya reklam mekanizması yoksa, enerji kontrolünü bypass et
        if (!isAdsenseConfigured || !showRewardedAd) {
          console.log('[Web RoomList] AdSense not configured or showRewardedAd not available, bypassing energy check for web');
          // Web'de enerji kontrolünü bypass et - direkt oda oluştur
          // Enerji harcama işlemini atla (aşağıda kontrol edilecek)
        } else {
          // Web'de reklam mekanizması varsa, normal akışı takip et
          Alert.alert(
            'Düşük Enerji',
            'Oda oluşturmak için 1 Enerji gerekir.\n\nReklam izleyerek enerjini yükselt.',
            [
              { 
                text: 'İptal', 
                style: 'cancel',
                onPress: () => {
                  setShowCreateDialog(false);
                }
              },
              { 
                text: 'Reklam İzle', 
                onPress: () => {
                  setShowCreateDialog(false);
                  showRewardedAd();
                }
              },
            ]
          );
          return;
        }
      } else {
        // Mobil platformlarda normal akış
        Alert.alert(
          'Düşük Enerji',
          'Oda oluşturmak için 1 Enerji gerekir.\n\nReklam izleyerek enerjini yükselt.',
          [
            { 
              text: 'İptal', 
              style: 'cancel',
              onPress: () => {
                setShowCreateDialog(false);
              }
            },
            { 
              text: 'Reklam İzle', 
              onPress: () => {
                setShowCreateDialog(false);
                showRewardedAd();
              }
            },
          ]
        );
        return;
      }
    }

    // Oda oluşturma işlemini başlat
    isCreatingRoomRef.current = true;

    if (Platform.OS === 'web') {
      console.log('[Web RoomList] Starting room creation, roomName:', trimmedName, 'radioId:', radio._id);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/radios/${radio._id}/rooms`,
        { roomName: trimmedName },
        { timeout: 5000 }
      );

      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Room created successfully:', response.data);
      }

      // Web'de AdSense yapılandırılmamışsa veya reklam mekanizması yoksa, enerji harcama işlemini atla
      if (Platform.OS === 'web' && (!isAdsenseConfigured || !showRewardedAd)) {
        if (Platform.OS === 'web') {
          console.log('[Web RoomList] Bypassing energy spending for web (AdSense not configured or no ad mechanism)');
        }
      } else {
        // Normal akış: enerji harca
        const energySpent = await spendTokens(1);
        if (!energySpent) {
          if (Platform.OS === 'web') {
            console.warn('[Web RoomList] Failed to spend tokens');
          }
          Alert.alert('Uyarı', 'Enerji güncellenemedi. Lütfen enerjinizi kontrol edin.');
        }
      }

      setRooms(prev => [...prev, response.data]);
      setShowCreateDialog(false);
      setNewRoomName('');

      if (Platform.OS === 'web') {
        console.log('[Web RoomList] Calling onSelectRoom with new room:', response.data);
      }
      setTimeout(() => {
        onSelectRoom(response.data);
      }, 100);
    } catch (error) {
      if (Platform.OS === 'web') {
        console.error('[Web RoomList] Room creation error:', error);
      } else {
        console.error('Oda oluşturma hatası:', error);
      }
      const message = error.response?.data?.error || 'Oda oluşturulamadı';
      Alert.alert('Hata', message);
    } finally {
      // Oda oluşturma işlemi tamamlandı (başarılı veya başarısız)
      isCreatingRoomRef.current = false;
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadRooms();
    setRefreshing(false);
  };

  const handleRoomSelect = async (room) => {
    // Oyun odaları için özel işlemler
    if (room.isGameRoom) {
      // Pişti oyunu için global oda listesini aç
      if (room.gameType === 'pisti' && onOpenPisti) {
        if (Platform.OS === 'web') {
          console.log('[Web RoomList] Opening Pişti room list with radio:', radio.name);
        }
        onOpenPisti({ stopPropagation: () => {} }, radio);
        return;
      }
      if (room.gameType === 'batak') {
        if (onOpenBatak) {
          onOpenBatak({ stopPropagation: () => {} }, radio);
        }
        return;
      }
      if (room.gameType === 'uno' && onOpenUno) {
        if (Platform.OS === 'web') {
          console.log('[Web RoomList] Opening UNO room list with radio:', radio.name);
        }
        onOpenUno({ stopPropagation: () => {} }, radio);
        return;
      }
      // Diğer oyun odaları için normal akış
      onSelectRoom(room);
      return;
    }

    // Normal odalar için enerji kontrolü
    if (tokens < 1) {
      if (Platform.OS === 'web') {
        // Web'de AdSense yapılandırılmamışsa veya reklam mekanizması yoksa, enerji kontrolünü bypass et
        if (!isAdsenseConfigured || !showRewardedAd) {
          console.log('[Web RoomList] AdSense not configured or showRewardedAd not available, bypassing energy check for room entry');
          // Web'de enerji kontrolünü bypass et - direkt odaya gir
          // Enerji harcama işlemini atla (aşağıda kontrol edilecek)
        } else {
          // Web'de reklam mekanizması varsa, normal akışı takip et
          Alert.alert(
            'Düşük Enerji',
            'Odaya girmek için 1 Enerji gerekir.\n\nReklam izleyerek enerjini yükselt.',
            [
              { 
                text: 'İptal', 
                style: 'cancel',
                onPress: () => {
                  // İşlemi durdur
                }
              },
              { 
                text: 'Reklam İzle', 
                onPress: () => {
                  showRewardedAd();
                }
              },
            ]
          );
          return;
        }
      } else {
        // Mobil platformlarda normal akış
        Alert.alert(
          'Düşük Enerji',
          'Odaya girmek için 1 Enerji gerekir.\n\nReklam izleyerek enerjini yükselt.',
          [
            { 
              text: 'İptal', 
              style: 'cancel',
              onPress: () => {
                // İşlemi durdur
              }
            },
            { 
              text: 'Reklam İzle', 
              onPress: () => {
                showRewardedAd();
              }
            },
          ]
        );
        return;
      }
    }

    // Web'de AdSense yapılandırılmamışsa veya reklam mekanizması yoksa, enerji harcama işlemini atla
    if (Platform.OS === 'web' && (!isAdsenseConfigured || !showRewardedAd)) {
      console.log('[Web RoomList] Bypassing energy spending for room entry (AdSense not configured or no ad mechanism)');
    } else {
      // Normal akış: enerjiyi harca
      const success = await spendTokens(1);
      if (!success) {
        Alert.alert('Hata', 'Enerji harcanamadı. Lütfen tekrar deneyin.');
        return;
      }
    }

    if (Platform.OS === 'web') {
      console.log('[Web RoomList] Calling onSelectRoom with room:', room?.roomId, room?.roomName);
    }
    onSelectRoom(room);
  };

  // RoomCard component for hover state
  const RoomCard = React.memo(({ item, handleRoomSelect }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        onMouseEnter={() => Platform.OS === 'web' && setIsHovered(true)}
        onMouseLeave={() => Platform.OS === 'web' && setIsHovered(false)}
      >
        <Card
          style={[
            styles.roomCard,
            Platform.OS === 'web' && isHovered && styles.roomCardHovered,
          ]}
          onPress={(e) => {
            // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
            if (e && e.stopPropagation) {
              e.stopPropagation();
            }
            handleRoomSelect(item);
          }}
        >
      <Card.Content>
        <View style={styles.roomHeader}>
          <Text style={styles.roomName}>{item.roomName}</Text>
          <View style={styles.roomInfo}>
            <Text style={styles.participantCount}>
              {item.participantCount} dinleyici
            </Text>
          </View>
        </View>
      </Card.Content>
    </Card>
    </View>
    );
  });

  const renderRoom = ({ item }) => (
    <RoomCard item={item} handleRoomSelect={handleRoomSelect} />
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            onPress={onClose}
            style={{
              width: 40,
              height: 40,
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: 8,
            }}
          >
            <RNText style={{ fontSize: 24, color: '#ffffff' }}>✕</RNText>
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <Text style={styles.radioName}>{radio.name}</Text>
            <Text style={styles.headerSubtitle}>Birlikte Dinle Odaları</Text>
          </View>
        </View>
      </View>

      {loading && rooms.length === 0 ? (
        <View style={styles.loadingContainer}>
          <Text>Yükleniyor...</Text>
        </View>
      ) : (
        <FlatList
          data={rooms}
          renderItem={renderRoom}
          keyExtractor={(item) => item.roomId}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={['#6200ee']}
              tintColor="#6200ee"
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                Henüz oda oluşturulmamış
              </Text>
              <Text style={styles.emptySubtext}>
                Yeni oda oluşturmak için + butonuna basın
              </Text>
            </View>
          }
        />
      )}

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={(e) => {
          if (Platform.OS === 'web' && e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (Platform.OS === 'web') {
            console.log('[Web RoomList] FAB pressed, opening create dialog');
          }
          setShowCreateDialog(true);
        }}
        label="Yeni Oda"
      />

      <Portal>
        <Dialog 
          visible={showCreateDialog} 
          onDismiss={() => {
            setShowCreateDialog(false);
            setNewRoomName('');
          }}
          dismissable={true}
        >
          <Dialog.Title>Yeni Oda Oluştur</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Oda Adı"
              value={newRoomName}
              onChangeText={setNewRoomName}
              placeholder="Örn: Pop Müzik Severler"
              mode="outlined"
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => {
              setShowCreateDialog(false);
              setNewRoomName('');
            }}>İptal</Button>
            <Button 
              onPress={(e) => {
                if (Platform.OS === 'web' && e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                if (Platform.OS === 'web') {
                  console.log('[Web RoomList] Create button pressed, newRoomName:', newRoomName);
                }
                if (newRoomName.trim()) {
                  createRoom();
                }
              }}
              disabled={!newRoomName.trim()}
            >
              Oluştur
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F1115',
    ...(Platform.OS === 'web' ? {
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      alignSelf: 'stretch',
      paddingHorizontal: 0,
    } : {}),
  },
  header: {
    backgroundColor: '#151A22',
    paddingTop: Platform.OS === 'web' ? 24 : 50,
    paddingBottom: Platform.OS === 'web' ? 24 : 20,
    paddingHorizontal: Platform.OS === 'web' ? 24 : 16,
    ...(Platform.OS === 'web' ? {
      borderRadius: 0,
      marginTop: 0,
      marginBottom: 0,
      boxShadow: 'none',
    } : {}),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    marginLeft: 8,
  },
  radioName: {
    color: '#ffffff',
    fontSize: Platform.OS === 'web' ? 24 : 18,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    color: '#ffffff',
    fontSize: Platform.OS === 'web' ? 16 : 14,
    opacity: 0.8,
    marginTop: 4,
  },
  listContent: {
    padding: Platform.OS === 'web' ? 24 : 16,
  },
  roomCard: {
    marginBottom: Platform.OS === 'web' ? 16 : 12,
    elevation: 2,
    backgroundColor: '#151A22',
    borderRadius: Platform.OS === 'web' ? 16 : 12,
    ...(Platform.OS === 'web' ? {
      padding: 20,
      borderWidth: 1,
      borderColor: '#2A2A2A',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
    } : {}),
  },
  roomCardHovered: {
    ...(Platform.OS === 'web' ? {
      transform: [{ translateY: -4 }],
      borderColor: '#FF4444',
      boxShadow: '0 12px 24px rgba(255, 68, 68, 0.15)',
      backgroundColor: '#1A1F28',
    } : {}),
  },
  roomHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roomName: {
    fontSize: Platform.OS === 'web' ? 22 : 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  roomInfo: {
    alignItems: 'flex-end',
  },
  participantCount: {
    fontSize: 14,
    color: '#A7B1C2',
    marginTop: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    color: '#B0B0B0',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#808080',
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    margin: Platform.OS === 'web' ? 24 : 16,
    right: 0,
    bottom: 0,
    backgroundColor: '#FF4444',
    ...(Platform.OS === 'web' ? {
      width: 64,
      height: 64,
    } : {}),
  },
});

